import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import CommitCard from '../components/CommitCard'
import './Wall.css'

const PAGE_SIZE = 20

export default function Wall() {
  const { user } = useAuth()
  const [tab, setTab] = useState('all') // 'all' | 'verified'
  const [commits, setCommits] = useState([])
  const [loading, setLoading] = useState(true)
  const [hasMore, setHasMore] = useState(true)

  const fetchCommits = useCallback(async (reset) => {
    setLoading(true)
    let q = supabase
      .from('commits')
      .select(`
        *,
        profiles!commits_user_id_fkey(username, display_name, full_name, avatar_url),
        projects!commits_project_id_fkey!inner(id, title, status, visibility),
        commit_likes(count),
        commit_comments(count)
      `)
      .eq('projects.visibility', 'public')
      .eq('projects.admin_hidden', false)
      .eq('is_removed', false)
      .order('created_at', { ascending: false })
      .limit(PAGE_SIZE)

    if (tab === 'verified') q = q.eq('projects.status', 'verified')

    if (!reset && commits.length > 0) {
      q = q.lt('created_at', commits[commits.length - 1].created_at)
    }

    const { data, error } = await q
    if (error) { console.error(error); setLoading(false); return }

    let rows = (data || []).map(c => ({
      ...c,
      like_count: c.commit_likes?.[0]?.count || 0,
      comment_count: c.commit_comments?.[0]?.count || 0,
    }))

    if (user && rows.length > 0) {
      const ids = rows.map(r => r.id)
      const [{ data: myLikes }, { data: myFlags }] = await Promise.all([
        supabase.from('commit_likes').select('commit_id').eq('user_id', user.id).in('commit_id', ids),
        supabase.from('commit_flags').select('commit_id').eq('user_id', user.id).in('commit_id', ids),
      ])
      const likedSet = new Set((myLikes || []).map(l => l.commit_id))
      const flaggedSet = new Set((myFlags || []).map(f => f.commit_id))
      rows = rows.map(r => ({ ...r, liked_by_me: likedSet.has(r.id), flagged_by_me: flaggedSet.has(r.id) }))
    }

    setHasMore(rows.length === PAGE_SIZE)
    setCommits(prev => reset ? rows : [...prev, ...rows])
    setLoading(false)
  }, [tab, user, commits])

  useEffect(() => { fetchCommits(true) }, [tab]) // eslint-disable-line

  return (
    <div className="wall-page page-fade">
      <div className="container container-narrow">
        <div className="wall-header">
          <h1>The wall</h1>
          <p>Human work, as it's being made. Every post here is a real moment from a real project.</p>
        </div>

        <div className="wall-tabs">
          <button className={`wall-tab ${tab === 'all' ? 'active' : ''}`} onClick={() => setTab('all')}>For you</button>
          <button className={`wall-tab ${tab === 'verified' ? 'active' : ''}`} onClick={() => setTab('verified')}>Verified only</button>
        </div>

        {commits.length === 0 && !loading ? (
          <div className="wall-empty">
            <h3>{tab === 'verified' ? 'No verified work yet' : 'The wall is empty'}</h3>
            <p>Be the first to post here.</p>
          </div>
        ) : (
          <div className="wall-feed">
            {commits.map(c => <CommitCard key={c.id} commit={c} onChange={() => {}} />)}
          </div>
        )}

        {loading && <div style={{ textAlign: 'center', padding: 40 }}><span className="loading-spinner" /></div>}

        {!loading && hasMore && commits.length > 0 && (
          <button className="btn btn-secondary wall-load-more" onClick={() => fetchCommits(false)}>
            Load more
          </button>
        )}
      </div>
    </div>
  )
}
